import React, { useState, useMemo, useEffect } from "react";
import { Card, CardHeader, CardBody, CardTitle, Row, Col } from "reactstrap";

const FolderTable = ({ data, title, renderTable, emptyMessage }) => {
  const [openFolders, setOpenFolders] = useState({});

  // sortConfig is now stored per folder key
  const [sortConfigByFolder, setSortConfigByFolder] = useState({});

  // toggle a folder's open/close state
  const toggleFolder = (subfolder) => {
    setOpenFolders((prev) => ({
      ...prev,
      [subfolder]: !prev[subfolder],
    }));
  };

  // group items by subFolder
  const groupedBySubFolder = useMemo(() => {
    return data.reduce((acc, item) => {
      if (item.subFolder) {
        if (!acc[item.subFolder]) acc[item.subFolder] = [];
        acc[item.subFolder].push(item);
      }
      return acc;
    }, {});
  }, [data]);

  const sortedSubFolders = useMemo(() => {
  return Object.keys(groupedBySubFolder).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}, [groupedBySubFolder]);

  const noSubFolderItems = useMemo(() => data.filter((i) => !i.subFolder), [data]);

  const hasSubfolders = Object.keys(groupedBySubFolder).length > 0;

  // ensure openFolders aligns with current subfolder keys
  useEffect(() => {
    const keys = Object.keys(groupedBySubFolder);
    setOpenFolders((prev) => {
      const next = {};
      keys.forEach((k) => {
        next[k] = prev[k] ?? false;
      });
      return next;
    });
  }, [Object.keys(groupedBySubFolder).join(",")]);

  // universal comparator
  const compare = (a, b, key) => {
    const aVal = a?.[key];
    const bVal = b?.[key];

    // null handling
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // numeric fields
    if (key === "fileSize") {
      return Number(aVal) - Number(bVal);
    }

    // dates
    if (key === "uploadDate" || key === "fileCreatedDate" || key.toLowerCase().includes("date")) {
      return new Date(aVal) - new Date(bVal);
    }

    // string fallback
    return String(aVal).localeCompare(String(bVal));
  };

  // sort based on per-folder config
  const sortList = (list, folderKey) => {
    const cfg = sortConfigByFolder[folderKey];
    if (!cfg?.key) return list;

    const sorted = [...list].sort((a, b) => compare(a, b, cfg.key));
    if (cfg.direction === "desc") sorted.reverse();

    return sorted;
  };

  // handle sorting within a specific folder
  const handleSort = (folderKey, sortKey) => {
    setSortConfigByFolder((prev) => {
      const prevCfg = prev[folderKey] || { key: null, direction: "asc" };

      if (prevCfg.key === sortKey) {
        // toggle direction
        return {
          ...prev,
          [folderKey]: {
            key: sortKey,
            direction: prevCfg.direction === "asc" ? "desc" : "asc",
          },
        };
      }

      return {
        ...prev,
        [folderKey]: { key: sortKey, direction: "asc" },
      };
    });
  };

  return (
    <Row>
      <Col md="12">
        <Card>
          <CardHeader>
            <CardTitle tag="h5" style={{ color: "#EC2B20" }}>
              {title}
            </CardTitle>
          </CardHeader>

          <CardBody>
            <div className="content">
              
              {noSubFolderItems.length > 0 && (
                <Card style={{ marginBottom: "12px" }}>
                  <CardBody>
                    {renderTable(
                      sortList(noSubFolderItems, ""),          
                      (key) => handleSort("", key),                
                      sortConfigByFolder[""] || {}                 
                    )}
                  </CardBody>
                </Card>
              )}

              {/* SUBFOLDERS */}
              {hasSubfolders && (
                <div style={{ paddingBottom: "15px", paddingLeft: "10px", paddingTop: "10px" }}>
                  <span style={{ fontWeight: "600", flex: 1 }}>Subfolders</span>
                </div>
              )}

              {sortedSubFolders.map((subfolder, idx) => (
                <div key={subfolder + "_" + idx} style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      background: "#f9f9f9",
                      padding: "8px 12px",
                      borderRadius: "8px",
                    }}
                    onClick={() => toggleFolder(subfolder)}
                  >
                    <i
                      className={`nc-icon nc-paper ${
                        openFolders[subfolder] ? "nc-folder-open" : "nc-folder-2"
                      }`}
                      style={{
                        fontSize: "22px",
                        color: "#EC2B20",
                        marginRight: "10px",
                      }}
                    />

                    <span style={{ fontWeight: "600", flex: 1 }}>{subfolder}</span>

                    <i
                      className={`nc-icon ${openFolders[subfolder] ? "nc-minimal-up" : "nc-minimal-down"}`}
                      style={{ fontSize: "18px", color: "#201E1F" }}
                    />
                  </div>

                  {openFolders[subfolder] && (
                    <Card style={{ marginTop: "10px" }}>
                      <CardBody>
                        {renderTable(
                          sortList(groupedBySubFolder[subfolder], subfolder), 
                          (key) => handleSort(subfolder, key),                 
                          sortConfigByFolder[subfolder] || {}                
                        )}
                      </CardBody>
                    </Card>
                  )}
                </div>
              ))}

              {data.length === 0 && <p style={{ paddingTop: "20px" }}>{emptyMessage}</p>}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default FolderTable;
